import { Component, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { finalize } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { ThemeFormComponent } from "./theme-form/theme-form.component";
import { Observable } from "rxjs";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { SpinnerLoaderComponent } from "../../../shared/components/spinner-loader/spinner-loader.component";
import {
  ICreateThemeReq,
  ITheme,
  IThemeReq,
  IUpdateThemeReq,
  ThemeStatus,
} from "../../../Utils/_models/themes.model";
import { IAppResponse } from "../../../Utils/_interceptors/network-error.model";
import { SelectModule } from "primeng/select";
import { ThemesService } from "../../../Utils/_services/admin/Themes/themes.service";
import { ConfirmService } from "../../../Utils/_services/confirmation-dialog.service";
import { TooltipModule } from "primeng/tooltip";


@Component({
  selector: "app-themes",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ThemeFormComponent,
    SearchBoxComponent,
    SpinnerLoaderComponent,
    SelectModule,
    TooltipModule
  ],
  templateUrl: "./themes.component.html",
  styleUrls: ["./themes.component.css"],
})
export class ThemesComponent implements OnInit {
  constructor(
    private themeService: ThemesService,
    private toastService: ToastrService,
    private confirmationService : ConfirmService
   
  ) {}
  pageSizes: number[] = [5, 10, 20, 50];
  searchTerm = signal<string | null>(null);
  pageSize = signal(2);
  currentPage = signal(1);
  totalPages = signal(0);
  totalCount = signal(0);
  themes = signal<ITheme[]>([]);
  originalThemes = signal<ITheme[]>([]);
  loading = signal(false);
  allSelected = signal(false);
  showThemeForm = signal(false);
  isEditMode = signal(false);
  editingTheme = signal<ITheme | null>(null);
  isSubmitting = signal(false);
  startIndex = signal(0);
  endIndex = signal(0);
  themeStatuses: { label: string; value: string }[] = [];
  selectedThemeStatus: ThemeStatus | null = null;
  currentSortField = signal<string | null>(null);
  currentSortOrder = signal<"asc" | "desc" | null>(null);
  includeDeleted = signal(false);
  hasSelection = signal(false);
  hasDeletedSelection = signal(false);
  previewImageUrl = signal<string | null>(null);

  ngOnInit(): void {
     this.pageSize.set(this.pageSizes[0]);
    this.themeStatuses = Object.values(ThemeStatus).map(l => ({
      label: l,
      value: l,
    }));
    this.fetchThemes();
  }

  onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.sortThemesLocal();
  }

  sortThemesLocal(): void {
    const field = this.currentSortField();
    const order = this.currentSortOrder();

    if (!field || !order) {
      this.themes.set([...this.originalThemes()]);
      this.updatePagination();
      return;
    }

    const direction = order === "asc" ? 1 : -1;
    const sorted = [...this.themes()];

    sorted.sort((a: any, b: any) => {
      let valA: any = a[field];
      let valB: any = b[field];

      if (field === 'name') {
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
      } else if (field === 'description') {
        valA = (a.description || '').toLowerCase();
        valB = (b.description || '').toLowerCase();
      } else if (field === 'participantsCount') {
        valA = Number(a.defaults?.participantsCount || 0);
        valB = Number(b.defaults?.participantsCount || 0);
      } else if (field === 'interestsCount') {
        valA = Number(a.defaults?.interestsCount || 0);
        valB = Number(b.defaults?.interestsCount || 0);
      } else if (field === 'entryFee') {
        valA = Number(a.defaults?.entryFee || 0);
        valB = Number(b.defaults?.entryFee || 0);
      } else if (field === 'status') {
        valA = a.isActive ? 1 : 0;
        valB = b.isActive ? 1 : 0;
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

    this.themes.set(sorted);
    this.updatePagination();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }

  filterChanged() {
    this.currentPage.set(1);
    this.fetchThemes();
  }

  fetchThemes(): void {
    this.loading.set(true);
    const req: IThemeReq = {
      page: this.currentPage(),
      limit: this.pageSize(),
      isActive:
        this.selectedThemeStatus == null
          ? null
          : this.selectedThemeStatus === ThemeStatus.Active,
      search: this.searchTerm(),
      sort: this.currentSortField(),
      sortOrder: this.currentSortField() ? this.currentSortOrder() : null,
      includeDeleted: this.includeDeleted(),
    };

    this.themeService
      .getAll(req)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: res => {
          if (res?.auth?.success) {
            const apiThemes = res?.data?.items || [];
            this.themes.set(apiThemes);
            this.originalThemes.set([...apiThemes]);
            this.totalCount.set(res?.data?.total || 0);
            this.currentPage.set(res?.data?.page || 1);
            this.pageSize.set(res?.data?.limit || 20);
            this.totalPages.set(res?.data?.totalPages || 0);
            if (this.currentSortField() && this.currentSortOrder()) {
              this.sortThemesLocal();
            } else {
              this.updatePagination();
            }
          }
        },
        error: err => {
          this.toastService.error("Error while fetching the themes");
        },
      });
    this.loading.set(true);
  }

  onSearch(value: string | null) {
    this.currentPage.set(1);
    this.searchTerm.set(value);
    this.fetchThemes();
  }

  onToggleSelectAll() {
    this.allSelected.update(v => !v);
    this.themes().forEach(x => {
      x.selected = this.allSelected();
    });
    this.updateSelectAllState();
  }


  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage.update(v => v + 1);
      this.updatePagination();
      this.fetchThemes();
    }
  }

  previousPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update(v => v - 1);
      this.updatePagination();
      this.fetchThemes();
    }
  }

  updatePagination() {
    this.totalPages.set(Math.ceil(this.totalCount() / this.pageSize()));
    this.startIndex.set((this.currentPage() - 1) * this.pageSize() + 1);
    this.endIndex.set(
      Math.min(this.currentPage() * this.pageSize(), this.totalCount())
    );

    this.updateSelectAllState();
  }

  updateSelectAllState() {
    const allThemes = this.themes();
    const selectedCount = allThemes.filter(theme => theme.selected).length;
    this.allSelected.set(
      selectedCount === allThemes.length && allThemes.length > 0
    );
    this.hasSelection.set(selectedCount > 0);
    this.hasDeletedSelection.set(allThemes.some(t => t.selected && !!t.deletedAt));
  }

  toggleIncludeDeleted() {
    this.includeDeleted.update(v => !v);
    this.fetchThemes();
  }

  openImagePreview(url: string | undefined): void {
    if (url) {
      this.previewImageUrl.set(url);
    }
  }

  closePreview(): void {
    this.previewImageUrl.set(null);
  }

  openImageInNewTab(url: string | undefined): void {
    if (url) {
      window.open(url, "_blank");
    }
  }

  deleteSelectedThemes() {
    const selectedThemes = this.themes().filter(t => t.selected);
    if (!selectedThemes.length) return;

    const deletedSelected = selectedThemes.filter(t => t.deletedAt);
    const activeSelected = selectedThemes.filter(t => !t.deletedAt);
    const isRestore = deletedSelected.length > 0;

    const header = isRestore ? 'Restore Themes' : 'Delete Themes';
    const bodyHeader = isRestore ? 'Restore Selected Themes' : 'Delete Selected Themes';
    const bodySubtitle = isRestore
      ? `You are about to restore ${deletedSelected.length} deleted theme(s).${activeSelected.length > 0 ? ` ${activeSelected.length} non-deleted theme(s) will also be deleted.` : ''}`
      : `Are you sure you want to delete ${activeSelected.length} selected theme(s)? This action cannot be undone.`;
    const yesButtonText =
      deletedSelected.length > 0 && activeSelected.length > 0
        ? 'Apply Changes'
        : isRestore
          ? 'Restore'
          : 'Delete';
    const yesBtnCss = isRestore ? 'btn-primary' : 'btn-danger';

    this.confirmationService.confirm({
      header,
      bodyHeader,
      bodySubtitle,
      yesButtonText,
      noButtonText: 'Cancel',
      yesBtnCss,
      noBtnCss: 'btn-secondary',
      width: '420px',
    }).then(confirmed => {
      if (confirmed) {
        const ids = selectedThemes.map(t => t._id);
        this.themeService.bulkDeleteThemes(ids).subscribe({
          next: () => {
            this.toastService.success("Selected themes deleted successfully");
            this.fetchThemes();
          },
          error: (err) => {
            this.toastService.error(err.message || 'Failed to delete themes');
          }
        });
      }
    });
  }

  prevPage() {
    if (this.currentPage() > 1) this.currentPage.update(v => v - 1);
  }

  trackById(index: number, item: ITheme): string | number {
    return item._id || index;
  }

  openCreateModal(): void {
    this.isEditMode.set(false);
    this.editingTheme.set(null);
    this.showThemeForm.set(true);
  }

  openEditModal(theme: ITheme): void {
    if (!theme?._id) return;
    this.isEditMode.set(true);
    this.editingTheme.set(theme);
    this.showThemeForm.set(true);
  }

  closeThemeForm(): void {
    this.showThemeForm.set(false);
    this.isEditMode.set(false);
    this.editingTheme.set(null);
  }

  onThemeFormSubmit(formData: ICreateThemeReq): void {
    if (this.isSubmitting()) return;

    this.isSubmitting.set(true);
    let req: IUpdateThemeReq;
    let obs: Observable<IAppResponse<ITheme>>;
    if (this.isEditMode() && this.editingTheme()?._id) {
      req = {
        id: this.editingTheme()?._id ?? "",
        name: formData.name,
        isActive: formData.isActive,
        image: formData.image,
        questionGroup: formData.questionGroup,
        roundsPerInterest: formData.roundsPerInterest,
        genericQuestionsDuration: formData.genericQuestionsDuration,
      };
      obs = this.themeService.updateTheme(req);
    } else {
      obs = this.themeService.createTheme(formData);
    }

    obs
      .pipe(
        finalize(() => {
          this.isSubmitting.set(false);
        })
      )
      .subscribe({
        next: res => {
          if (res.auth.success) {
            const wasEdit = this.isEditMode;
            this.closeThemeForm();
            this.fetchThemes();
            this.toastService.success(
              wasEdit()
                ? "Theme updated successfully"
                : "Theme created successfully"
            );
          }
        },
        error: err => {
          const msg =
            err?.error?.message ||
            (this.isEditMode()
              ? "Failed to update theme"
              : "Failed to create theme");
          this.toastService.error(msg);
        },
      });
  }
    onPageSizeChange(newSize: number) {
    this.pageSize.set(newSize);
    this.currentPage.set(1);
    this.fetchThemes();
  }

  async deleteTheme(theme: ITheme): Promise<void>{
   const confirmed = await this.confirmationService.confirm({
     header: '',
      bodyHeader: 'Delete theme',
      bodySubtitle: `Are you sure you want to delete this ${theme.name}? This action cannot be undone.`,
      yesButtonText: 'Delete',
      noButtonText: 'Cancel',
      yesBtnCss: 'btn-danger',
      noBtnCss: 'btn-secondary',
      width: '420px',

   });
   if(confirmed){
  this.themeService.bulkDeleteThemes([theme._id]).subscribe({
      next:()=>{
        this.toastService.success("Theme deleted successfully");
        this.fetchThemes();
      },
      error:(err)=>{
        this.toastService.error(err.message || 'Failed to delete Theme')
      }
    })
   }
  }

}
